import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  ONBOARDING_STEPS,
  stepPath,
  getNextOnboardingStep,
  needsEmailVerification,
} from "@/lib/onboardingSteps";

// Re-export the helpers so any legacy imports of them from "@/lib/guards"
// continue to resolve.
export { ONBOARDING_STEPS, getNextOnboardingStep, needsEmailVerification };

/**
 * Guard for authenticated app routes. Redirects:
 *   - not authed → /login
 *   - authed but email not verified → /verify
 *   - authed but onboarding incomplete → to next step
 *   - authed & complete → children
 *
 * Also holds still (renders null) while a welcome splash is active — the
 * splash owns the post-login route transition, so guards must not race
 * ahead and navigate mid-animation.
 */
export function RequireAuth({ children }) {
  const { authed, onboarding, loading, welcome } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
        <div className="text-sm">Loading…</div>
      </div>
    );
  }

  // Welcome splash owns the route transition after any successful login.
  // Don't fire a Navigate here — the splash's onFinish handler will.
  if (welcome) return null;

  if (!authed) return <Navigate to="/login" replace state={{ from: location }} />;

  // Email not yet verified → force /verify (unless already there)
  if (needsEmailVerification(onboarding) && location.pathname !== "/verify") {
    return <Navigate to="/verify" replace />;
  }

  // Check onboarding completion
  const nextStep = getNextOnboardingStep(onboarding);
  if (nextStep && !location.pathname.startsWith("/onboarding")) {
    return <Navigate to={stepPath(nextStep)} replace />;
  }

  return children;
}

/**
 * Guard for onboarding routes. Requires auth and NOT complete.
 * If complete → redirect to /home.
 */
export function RequireOnboarding({ children }) {
  const { authed, onboarding, loading, welcome } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
        <div className="text-sm">Loading…</div>
      </div>
    );
  }
  if (welcome) return null;
  if (!authed) return <Navigate to="/login" replace />;
  if (needsEmailVerification(onboarding)) return <Navigate to="/verify" replace />;
  const next = getNextOnboardingStep(onboarding);
  if (!next) return <Navigate to="/home" replace />;
  return children;
}

/**
 * Public-only routes: if authed & onboarded, redirect to /home.
 */
export function PublicOnly({ children }) {
  const { authed, onboarding, loading, welcome } = useAuth();
  if (loading) return null;
  // Welcome splash owns the transition after login — don't preemptively
  // Navigate to /home or /onboarding/basics while it's animating. This is
  // the fix for the split-second onboarding-screen flash after Continue
  // with Google / Apple / email login.
  if (welcome) return null;
  if (authed) {
    if (needsEmailVerification(onboarding)) return <Navigate to="/verify" replace />;
    const next = getNextOnboardingStep(onboarding);
    if (next) return <Navigate to={stepPath(next)} replace />;
    return <Navigate to="/home" replace />;
  }
  return children;
}
